import { exifPosition, exifSegment, withPosition } from './jpegMetadata';
import type { PhotoPosition } from './jpegMetadata';

/**
 * The position for a photo the camera button takes.
 *
 * Android's camera, opened from a web page or the app's WebView, writes no
 * location into the picture — even with geotagging switched on in the camera.
 * So the location is asked for at the moment the button is pressed, while the
 * camera is still open, and written into the photo if it came back without one
 * (see `jpegMetadata.withPosition`). A picture from the gallery gets nothing:
 * it was taken somewhere else, at some other time.
 *
 * Works offline: the GPS receiver needs no network.
 */

/** How long a fix may take from the button press: GPS from cold is slow. */
const FIX_TIMEOUT_MS = 60_000;

/** A fix this young is as good as a new one — the crew stands where it stood. */
const FIX_MAX_AGE_MS = 60_000;

/** How long a photo that is back from the camera waits for a missing fix. */
const PHOTO_WAIT_MS = 10_000;

/**
 * Start asking for the device's position. Never rejects: no permission, no
 * location service, no fix in time — all resolve to null.
 */
export function requestPhotoPosition(): Promise<PhotoPosition | null> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
        return Promise.resolve(null);
    }
    return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
            ({ coords, timestamp }) =>
                resolve({
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    altitude: coords.altitude,
                    accuracy: coords.accuracy,
                    timestamp
                }),
            () => resolve(null),
            {
                enableHighAccuracy: true,
                maximumAge: FIX_MAX_AGE_MS,
                timeout: FIX_TIMEOUT_MS
            }
        );
    });
}

/**
 * `photo` with the position written in, if it is a JPEG without one and the
 * fix arrives in time. Anything else comes back unchanged.
 */
export async function withPhotoPosition(
    photo: Blob,
    pendingFix: Promise<PhotoPosition | null>
): Promise<Blob> {
    if (photo.type !== 'image/jpeg') {
        return photo;
    }
    const bytes = new Uint8Array(await photo.arrayBuffer());
    if (exifPosition(exifSegment(bytes))) {
        return photo;
    }
    const position = await Promise.race([
        pendingFix,
        new Promise<null>((resolve) => {
            setTimeout(() => resolve(null), PHOTO_WAIT_MS);
        })
    ]);
    if (!position) {
        return photo;
    }
    return new Blob([withPosition(bytes, position)], { type: 'image/jpeg' });
}
